import {
  BEARD_DAYS,
  beardStageOf,
  hairstyleIndex,
  outfitIndex,
  outfitOf,
  parseLook,
  type Hairstyle,
  type Look,
} from '@secretario/shared';

/**
 * Visual vivo no aparelho: a barba que o personagem a traço mostra agora.
 *
 * O estágio é calculado ao abrir o app (e ao voltar depois de um dia fora da tela),
 * nunca com a pessoa olhando: a troca acontece fora de vista. Quando ela pede para
 * fazer a barba, a passada apaga a barba na frente dela; quando pede outro estágio
 * (só a sombra, o máximo), a barba troca devagar. O rig do traço (`tracoRig`) lê
 * daqui a cada quadro.
 */
export const LOOK_CHANGED = 'secretario:visual';

/**
 * "Faz a barba", em segundos: a mão sobe pela esquerda, atravessa o queixo com a
 * passada (a barba some logo atrás dos dedos), volta ao queixo, passa a mão
 * ("ficou bom") e desce. Na barba rala (só a sombra) a passada sozinha mal se via:
 * é a mão atravessando que mostra o gesto.
 */
const HAND_UP = 0.45;
const SHAVE_SECONDS = 1.8;
const HAND_BACK = 0.35;
const HAND_RUB = 0.9;
const HAND_DOWN = 0.5;
const SHAVE_TOTAL = HAND_UP + SHAVE_SECONDS + HAND_BACK + HAND_RUB + HAND_DOWN + 0.5;
/**
 * Geometria do desenho (gen_traco.py, prancheta de 1088 px): a borda da passada vai
 * de x=300 a x=840, a ponta dos dedos fica em x≈672 com a mão no queixo, e `maoX`
 * de -1 a 1 desloca a mão 400 px para cada lado.
 */
const EDGE_FROM = 300;
const EDGE_TO = 840;
const FINGERTIP = 672;
const HAND_REACH = 400;
/** Vai-e-vem no queixo: ±9 px. */
const RUB = 9 / HAND_REACH;
const handAtEdge = (progress: number) =>
  (EDGE_FROM + (EDGE_TO - EDGE_FROM) * progress - FINGERTIP + 6) / HAND_REACH;
/** Troca de estágio pedida (sem barbear): a barba aparece ou some devagar. */
const BLEND_SECONDS = 1.4;

/** Troca de roupa na frente da pessoa: ele pisca e, de olhos fechados, já está trocado. */
const CHANGE_SECONDS = 0.45;
const CHANGE_SWAP = 0.18;

type State = {
  look: Look | null;
  /** Roupa mostrada (número do arquivo) e a troca em andamento. */
  roupa: number;
  change: { from: number; at: number } | null;
  /** Última conferência da hora (o pijama entra sozinho à noite). */
  hourAt: number;
  /** Roupa experimentada em Mudar o jeito (antes de salvar). */
  preview: number | null;
  /** Óculos experimentados em Mudar o jeito (antes de salvar). */
  glassesPreview: boolean | null;
  /** Penteado experimentado em Mudar o jeito (antes de salvar). */
  hairstylePreview: Hairstyle | null;
  shown: number;
  /** Dia (AAAA-MM-DD) em que o estágio mostrado foi calculado. */
  day: string;
  shave: { from: number; at: number } | null;
  blend: { from: number; to: number; at: number } | null;
};

const state: State = {
  look: null,
  shown: 1,
  day: '',
  shave: null,
  blend: null,
  roupa: 0,
  change: null,
  hourAt: 0,
  preview: null,
  glassesPreview: null,
  hairstylePreview: null,
};

const outfitNow = (look: Look) => outfitIndex(outfitOf(look, new Date()));

const today = () => new Date().toISOString().slice(0, 10);
const now = () => performance.now() / 1000;

function stageNow(look: Look): number {
  return beardStageOf(look, new Date());
}

/** O perfil chegou ou mudou: decide se troca fora de vista, anima ou mantém. */
export function syncLook(preferences: Record<string, unknown> | null | undefined): void {
  const next = parseLook(preferences);
  const previous = state.look;
  state.look = next;
  if (!previous) {
    state.shown = stageNow(next);
    state.day = today();
    state.roupa = outfitNow(next);
    return;
  }
  const roupa = outfitNow(next);
  if (roupa !== state.roupa) {
    state.change = { from: state.roupa, at: now() };
    state.roupa = roupa;
  }
  const before = previous.beard;
  const after = next.beard;
  const changed =
    JSON.stringify(before ?? null) !== JSON.stringify(after ?? null) && after !== undefined;
  if (!changed) return;
  const target = stageNow(next);
  const shaved = !!before && Date.parse(after.since) > Date.parse(before.since) && target === 0;
  if (shaved && state.shown > 0) {
    state.shave = { from: state.shown, at: now() };
  } else if (target !== state.shown) {
    state.blend = { from: state.shown, to: target, at: now() };
  }
  state.shown = target;
}

/** O app voltou à tela: se virou o dia, a barba já cresceu enquanto ninguém via. */
export function refreshLookOnReturn(): void {
  if (!state.look || state.shave || state.blend) return;
  if (state.day === today()) return;
  state.shown = stageNow(state.look);
  state.day = today();
}

/** Primeira vez: começa a contar os dias a partir da sombra do desenho original. */
export function initialLook(): Look {
  const since = new Date(Date.now() - BEARD_DAYS[1] * 24 * 60 * 60 * 1000 - 60_000).toISOString();
  return { beard: { since, max: 4, hold: null } };
}

export type LookFrame = {
  /** Estágio para o arquivo (fracionado durante a troca devagar). */
  barba: number;
  /** Passada de barbear, 0 a 1. */
  barbear: number;
  /** 0 fora; sobe a 1 enquanto barbeia (olhos fechados, sorriso de canto) e desce depois. */
  savor: number;
  /** Mão no queixo: 0 fora do quadro, 1 encostada; `maoX` é o vai-e-vem (-1 a 1). */
  mao: number;
  maoX: number;
  /** Roupa (número do arquivo, `OUTFITS`). */
  roupa: number;
  /** Óculos escuros na cabeça (número `oculos`): 0 sem, 1 com. */
  oculos: number;
  /** Penteado (número `penteado`, `HAIRSTYLES`); só a nova personagem lê. */
  penteado: number;
};

const ease = (value: number) => {
  const t = Math.min(1, Math.max(0, value));
  return t * t * (3 - 2 * t);
};

/** O que o rig escreve neste quadro. */
export function lookFrame(): LookFrame {
  const t = now();
  // Virou a noite (ou o dia) com o app aberto: o pijama entra numa piscada.
  if (state.look && t - state.hourAt > 30) {
    state.hourAt = t;
    const roupa = outfitNow(state.look);
    if (roupa !== state.roupa && !state.change) {
      state.change = { from: state.roupa, at: t };
      state.roupa = roupa;
    }
  }
  let roupa = state.preview ?? state.roupa;
  let blink = 0;
  if (state.change) {
    const elapsed = t - state.change.at;
    if (elapsed < CHANGE_SECONDS) {
      blink = Math.sin((elapsed / CHANGE_SECONDS) * Math.PI);
      if (elapsed < CHANGE_SWAP) roupa = state.change.from;
    } else {
      state.change = null;
    }
  }
  const frame = beardFrame(t);
  const oculos = (state.glassesPreview ?? state.look?.sunglasses ?? false) ? 1 : 0;
  const penteado = hairstyleIndex(state.hairstylePreview ?? state.look?.hairstyle);
  return { ...frame, roupa, oculos, penteado, savor: Math.max(frame.savor, blink) };
}

function beardFrame(t: number): Omit<LookFrame, 'roupa' | 'oculos' | 'penteado'> {
  if (state.shave) {
    const elapsed = t - state.shave.at;
    if (elapsed < SHAVE_TOTAL) {
      const from = state.shave.from;
      // Olhos fechando no começo e abrindo quando a mão desce.
      const down = HAND_UP + SHAVE_SECONDS + HAND_BACK + HAND_RUB;
      const savor =
        elapsed < down ? ease(elapsed / 0.4) : 1 - ease((elapsed - down) / (HAND_DOWN + 0.5));
      if (elapsed < HAND_UP) {
        // A mão sobe já à esquerda, com os dedos na borda de onde a passada começa.
        return {
          barba: from,
          barbear: 0,
          savor,
          mao: ease(elapsed / HAND_UP),
          maoX: handAtEdge(0),
        };
      }
      const shaving = elapsed - HAND_UP;
      if (shaving < SHAVE_SECONDS) {
        const progress = ease(shaving / SHAVE_SECONDS);
        return { barba: from, barbear: progress, savor, mao: 1, maoX: handAtEdge(progress) };
      }
      const after = shaving - SHAVE_SECONDS;
      if (after < HAND_BACK) {
        // Rosto liso; a mão volta ao queixo.
        return {
          barba: 0,
          barbear: 0,
          savor,
          mao: 1,
          maoX: handAtEdge(1) * (1 - ease(after / HAND_BACK)),
        };
      }
      const rubbing = after - HAND_BACK;
      if (rubbing < HAND_RUB) {
        return {
          barba: 0,
          barbear: 0,
          savor,
          mao: 1,
          maoX: Math.sin((rubbing / HAND_RUB) * Math.PI * 4) * RUB,
        };
      }
      const mao = 1 - ease((rubbing - HAND_RUB) / HAND_DOWN);
      return { barba: 0, barbear: 0, savor, mao, maoX: 0 };
    }
    state.shave = null;
  }
  if (state.blend) {
    const progress = (t - state.blend.at) / BLEND_SECONDS;
    if (progress < 1) {
      const { from, to } = state.blend;
      return { barba: from + (to - from) * ease(progress), barbear: 0, savor: 0, mao: 0, maoX: 0 };
    }
    state.blend = null;
  }
  return { barba: state.shown, barbear: 0, savor: 0, mao: 0, maoX: 0 };
}

/** Mudar o jeito: veste a roupa tocada no personagem da tela (null volta à de verdade). */
export function previewOutfit(roupa: number | null): void {
  const shown = state.preview ?? state.roupa;
  const next = roupa ?? state.roupa;
  if (next !== shown) state.change = { from: shown, at: now() };
  state.preview = roupa;
}

/** Óculos experimentados em Mudar o jeito: aparecem no personagem do topo antes de salvar. */
/** Mudar o jeito: experimenta um penteado na personagem da tela (null volta ao de verdade). */
export function previewHairstyle(hairstyle: Hairstyle | null): void {
  state.hairstylePreview = hairstyle;
}

export function previewGlasses(on: boolean | null): void {
  state.glassesPreview = on;
}

/** Para testes: volta ao estado de quem acabou de abrir o app. */
export function resetLook(): void {
  Object.assign(state, {
    look: null,
    shown: 1,
    day: '',
    shave: null,
    blend: null,
    roupa: 0,
    change: null,
    hourAt: 0,
    preview: null,
    glassesPreview: null,
    hairstylePreview: null,
  });
}
